/**
 * CreateKartotekaModal — WSPÓLNY modal „Utwórz kartotekę własną od nowa".
 *
 * Jeden komponent dla tenanta ORAZ admina (panel ITE) — walidacja nazwy/slugu pochodzi
 * z `@saas/shared` (slugifyModuleName + checkModuleSlugFormat), więc reguły są identyczne
 * wszędzie. Różni się tylko sam ZAPIS (endpoint tenant vs admin) — wstrzykiwany propem
 * `submitFn`, żeby nie duplikować formularza per scope.
 *
 * Pola: nazwa (wymagana), slug (generowany z nazwy, edytowalny), ikona, kolor.
 * Slug walidowany na żywo: format + rezerwacja (kolizja z kartoteką systemową) + unikalność
 * względem `existingSlugs` (podpowiedź kliencka; backend ma ostatnie słowo).
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { checkModuleSlugFormat, slugifyModuleName } from '@saas/shared';
import { isAxiosError } from 'axios';

import { Button } from '@/components/Button';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { IconPicker } from '@/components/modules/IconPicker';
import { useToast } from '@/contexts/ToastContext';

/** Wynik zapisu — minimalny kontrakt wspólny dla scope'ów. */
export interface CreatedKartoteka {
  id?: string;
  slug: string;
  name: string;
}

export interface CreateKartotekaInput {
  name: string;
  slug: string;
  icon: string;
  color: string;
}

export interface CreateKartotekaModalProps {
  open: boolean;
  onClose: () => void;
  /** Zapis scope-specific (tenant: POST /modules; admin: własny endpoint). */
  submitFn: (input: CreateKartotekaInput) => Promise<CreatedKartoteka>;
  /** Wywoływane po sukcesie (np. refetch + redirect do buildera). */
  onCreated: (result: CreatedKartoteka) => void;
  /** Slugi istniejących kartotek — do klienckiej podpowiedzi „już zajęty". */
  existingSlugs?: string[];
  /** Nazwy istniejących kartotek (globalnie) — kliencka podpowiedź „nazwa zajęta". */
  existingNames?: string[];
}

const PRESET_COLORS = [
  '#3B82F6',
  '#10B981',
  '#F59E0B',
  '#EF4444',
  '#8B5CF6',
  '#EC4899',
  '#06B6D4',
  '#6B7280',
];

export function CreateKartotekaModal({
  open,
  onClose,
  submitFn,
  onCreated,
  existingSlugs = [],
  existingNames = [],
}: CreateKartotekaModalProps) {
  const { t } = useTranslation();
  const toast = useToast();

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [icon, setIcon] = useState('Database');
  const [color, setColor] = useState('#3B82F6');
  const [submitting, setSubmitting] = useState(false);

  // Reset przy każdym otwarciu.
  useEffect(() => {
    if (open) {
      setName('');
      setSlug('');
      setSlugTouched(false);
      setIcon('Database');
      setColor('#3B82F6');
      setSubmitting(false);
    }
  }, [open]);

  // Slug efektywny: dopóki user nie ruszył pola, generujemy z nazwy.
  const effectiveSlug = slugTouched ? slug.trim().toLowerCase() : slugifyModuleName(name);

  const slugProblem = useMemo(() => {
    if (!name.trim() && !effectiveSlug) return null; // pusty formularz — nie strasz
    const format = checkModuleSlugFormat(effectiveSlug);
    if (format) return format;
    if (existingSlugs.includes(effectiveSlug)) return 'taken' as const;
    return null;
  }, [name, effectiveSlug, existingSlugs]);

  const slugError =
    slugProblem === 'invalid'
      ? t('kartotekaCreate.slugInvalid')
      : slugProblem === 'reserved'
        ? t('kartotekaCreate.slugReserved')
        : slugProblem === 'taken'
          ? t('kartotekaCreate.slugTaken')
          : undefined;

  // Nazwa zajęta (globalnie) — kliencka podpowiedź; backend ma ostatnie słowo (409 NAME_TAKEN).
  const nameTaken = useMemo(() => {
    const trimmed = name.trim().toLowerCase();
    if (!trimmed) return false;
    return existingNames.some((n) => n.trim().toLowerCase() === trimmed);
  }, [name, existingNames]);

  const canSubmit =
    Boolean(name.trim()) && Boolean(effectiveSlug) && !slugProblem && !nameTaken && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const result = await submitFn({ name: name.trim(), slug: effectiveSlug, icon, color });
      toast.show({ variant: 'success', title: t('kartotekaCreate.created') });
      onCreated(result);
      onClose();
    } catch (err) {
      // Mapowanie błędów backendu: NAME_TAKEN (nazwa zajęta) oraz SLUG_INVALID + reason.
      const errorCode = isAxiosError(err)
        ? (err.response?.data?.error as string | undefined)
        : undefined;
      const reason = isAxiosError(err)
        ? (err.response?.data?.reason as string | undefined)
        : undefined;
      const description =
        errorCode === 'NAME_TAKEN'
          ? t('kartotekaCreate.nameTaken')
          : reason === 'taken'
            ? t('kartotekaCreate.slugTaken')
            : reason === 'reserved'
              ? t('kartotekaCreate.slugReserved')
              : reason === 'invalid'
                ? t('kartotekaCreate.slugInvalid')
                : undefined;
      toast.show({
        variant: 'error',
        title: t('kartotekaCreate.createError'),
        description,
      });
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('kartotekaCreate.title')}
      description={t('kartotekaCreate.description')}
    >
      <div className="space-y-4" data-testid="create-kartoteka-modal">
        <FormField label={t('kartotekaCreate.nameLabel')} required>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('kartotekaCreate.namePlaceholder')}
            error={nameTaken ? t('kartotekaCreate.nameTaken') : undefined}
            autoFocus
            maxLength={100}
            data-testid="create-kartoteka-name"
          />
        </FormField>

        <Input
          label={t('kartotekaCreate.slugLabel')}
          hint={t('kartotekaCreate.slugHint')}
          value={effectiveSlug}
          onChange={(e) => {
            setSlugTouched(true);
            setSlug(e.target.value);
          }}
          error={slugError}
          placeholder="np. rejestr_szkolen"
          data-testid="create-kartoteka-slug"
        />

        <div className="grid grid-cols-2 gap-4">
          <FormField label={t('kartotekaCreate.iconLabel')}>
            <IconPicker value={icon} onChange={setIcon} accentColor={color} />
          </FormField>

          <FormField label={t('kartotekaCreate.colorLabel')}>
            <div className="flex flex-wrap gap-2" data-testid="create-kartoteka-color">
              {PRESET_COLORS.map((c) => {
                const selected = color === c;
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setColor(c)}
                    aria-label={c}
                    aria-pressed={selected}
                    className={
                      'h-8 w-8 rounded-full transition ' +
                      (selected ? 'ring-2 ring-offset-2 ring-text-primary' : 'hover:scale-110')
                    }
                    style={{ backgroundColor: c }}
                  />
                );
              })}
            </div>
          </FormField>
        </div>
      </div>

      <div className="mt-6 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          loading={submitting}
          disabled={!canSubmit}
          data-testid="create-kartoteka-submit"
        >
          {t('kartotekaCreate.submit')}
        </Button>
      </div>
    </Modal>
  );
}
